import { EmploymentNeed, FieldValuesType, Scope, SubmitApplicationOk, SubmitPayloadType } from '../types';
import { CalculatorStateProps } from '../branded-consumer-loan';
import { employmentOptions } from '../hooks/use-employment-fields';

type EmploymentFields = Partial<Record<keyof Omit<EmploymentNeed, 'applicantEmploymentMonthFrom' | 'applicantEmploymentMonthUntil'>, string>>;

export const useLoanSubmit = (scope: Scope = 'carla_se') => {
    const sanitizeNumber = (value: string | number) => `${value}`?.replace(/[^\d]+/g, '');

    const extractEmploymentFields = (fields: FieldValuesType, needs: EmploymentNeed, coapplicant: boolean = false) => {
        const employmentFields: EmploymentFields = {};

        if (needs?.applicantEmploymentYearFrom) {
            const year = `${fields.applicantEmploymentYearFrom}`.slice(-2);
            employmentFields.applicantEmploymentYearFrom = `${year}${fields.applicantEmploymentMonthFrom}`;
        }

        if (needs?.applicantEmploymentYearUntil && fields.applicantEmploymentYearUntil) {
            const year = `${fields.applicantEmploymentYearUntil}`.slice(-2);
            employmentFields.applicantEmploymentYearUntil = `${year}${fields.applicantEmploymentMonthUntil}`;
        }

        if (needs.applicantEmployer) {
            employmentFields.applicantEmployer = fields.applicantEmployer;
        }

        if (needs.employmentTypeQualifying) {
            employmentFields.employmentTypeQualifying = fields.employmentTypeQualifying;
        }

        if (coapplicant && needs.coapplicantEmploymentYearFrom) {
            const year = `${fields.coapplicantEmploymentYearFrom}`.slice(-2);
            employmentFields.coapplicantEmploymentYearFrom = `${year}${fields.coapplicantEmploymentMonthFrom}`;
        }

        if (coapplicant && needs.coapplicantEmploymentYearUntil && fields.coapplicantEmploymentYearUntil) {
            const year = `${fields.coapplicantEmploymentYearUntil}`.slice(-2);
            employmentFields.coapplicantEmploymentYearUntil = `${year}${fields.coapplicantEmploymentMonthUntil}`;
        }

        if (coapplicant && needs.coapplicantEmployer) {
            employmentFields.coapplicantEmployer = fields.coapplicantEmployer;
        }

        if (coapplicant && needs.coapplicantEmploymentTypeQualifying) {
            employmentFields.coapplicantEmploymentTypeQualifying = fields.coapplicantEmploymentTypeQualifying;
        }

        return employmentFields;
    };

    function normalizeSsn(input: string | number) {
        const digits = `${input}`.replace(/\D/g, '');

        if (digits.length === 12) {
            return digits.slice(2);
        }

        return digits;
    }

    const submit = async (fields: FieldValuesType, calculator: CalculatorStateProps) => {
        try {
            const option = employmentOptions.find(option => option.value === fields.applicantEmploymentType);
            const needs = option?.needs ?? {};
            const employmentFields = extractEmploymentFields(fields, needs);

            let payload: SubmitPayloadType = {
                applicantGovernmentId: normalizeSsn(fields.governmentId),
                amount: parseInt(sanitizeNumber(calculator.loanAmount)),
                time: calculator.tenorYears * 12,
                applicantMobileNumber: fields.phoneMobile,
                applicantEmailAddress: fields.contactEmail,
                purposeOfLoan: fields.purposeOfLoan,
                applicantMaritalStatus: fields.applicantMaritalStatus,
                numberChildrenNotOfAge: fields.numberChildrenNotOfAge,
                applicantHabitationForm: fields.applicantHabitationForm,
                applicantEmploymentType: fields.applicantEmploymentType,
                ...employmentFields,
                applicantMonthlyIncomeGross: sanitizeNumber(fields.montlyIncomeGross),
                householdLivingExpenses: sanitizeNumber(fields.livingExpenses),
                householdOtherLoanExpenses: sanitizeNumber(fields.otherLoanExpenses),
                payoutAccountNumber: sanitizeNumber(`${fields.payoutAccount.accountNumber}`),
                payoutClearingNumber: sanitizeNumber(`${fields.payoutAccount.clearingNumber}`),
                approveConditions: String(fields.approveConditions),
                agreementSignType: fields.agreementSignType,
            };

            if (fields.insurance) {
                payload.insurance = 'true';
            }

            if (fields.coapplicantBool) {
                const option = employmentOptions.find(option => option.value === fields.coapplicantEmploymentType);
                const needs = option?.needs ?? {};
                const coapplicantEmploymentFields = extractEmploymentFields(fields, needs, true);

                payload = {
                    ...payload,
                    coapplicantGovernmentId: normalizeSsn(fields.coapplicantGovernmentId),
                    coapplicantMobileNumber: fields.coapplicantMobileNumber,
                    coapplicantEmailAddress: fields.coapplicantEmailAddress,
                    coapplicantMaritalStatus: fields.coapplicantMaritalStatus,
                    coapplicantHabitationForm: fields.coapplicantHabitationForm,
                    ...coapplicantEmploymentFields,
                    coapplicantMonthlyIncomeGross: sanitizeNumber(fields.coapplicantMonthlyIncomeGross),
                };
            }

            const response = await fetch(`/api/consumer-loan/submit-application/${scope}`, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    credentials: 'include',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw {
                    message: 'Något gick fel, vänligen försök igen.',
                    status: response.status,
                };
            }

            const data = await response.json();

            return data as SubmitApplicationOk;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    return { submit };
};
